// ExerciseHistoryService.js — migrated to the OpenAPI-generated client.
//
// Adapter-first: every function keeps its pre-migration signature + return shape. Callers read
// the payload off `res.data` directly (the metadata/programExercise/weight-series object, or the
// Spring Page's `.content`/`.last`), so we return `{data}`. Auth + X-Timezone are injected by
// configureApi's interceptor, so the per-call token reads are gone.
//
// Note: updateProgramExerciseRangesById targets the program controller
// (/admin/program/exercise/{id}/range), so it maps to adminProgramControllerUpdateExerciseRange
// while the rest map to the adminExerciseHistoryController.
import {
    adminExerciseHistoryControllerGetMetadata,
    adminExerciseHistoryControllerGetSessionHistory,
    adminExerciseHistoryControllerGetCurrentProgramExercise,
    adminExerciseHistoryControllerUpdateProgramExerciseRanges,
    adminExerciseHistoryControllerGetWeightSeriesInfo,
    adminExerciseHistoryControllerUpdateWeightSet,
    adminProgramControllerUpdateExerciseRange,
} from '../api/generated';

// Re-throw an SDK error as an axios-shaped error so existing catch handlers that read
// `error.response?.data?.errorCode` / `.message` keep working exactly as they did on axios.
const asAxiosError = (error, response) => {
    /** @type {any} */
    const err = new Error(error?.errorMessage || error?.message || 'Request failed');
    err.response = {data: error, status: response?.status};
    return err;
};

export const getExerciseHistoryMetadata = async (traineeId, exerciseId, fromDate, toDate) => {
    const query = {};
    if (fromDate) query.fromDate = fromDate;
    if (toDate) query.toDate = toDate;

    const {data, error, response} = await adminExerciseHistoryControllerGetMetadata({
        path: {traineeId, exerciseId},
        query,
    });
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const getExerciseHistorySessions = async (traineeId, exerciseId, page = 0, size = 20) => {
    const {data, error, response} = await adminExerciseHistoryControllerGetSessionHistory({
        path: {traineeId, exerciseId},
        query: {page, size},
    });
    if (error) throw asAxiosError(error, response);
    return {data};
};

/**
 * The trainee's CURRENT target (weight/rep goal) for an exercise, or `{data: null}` when there
 * isn't one.
 *
 * A 404 here is a normal answer, not a failure. The server resolves the target from the trainee's
 * ACTIVE program (AdminProgramService.getCurrentProgramExercise -> findByTraineeAndActiveIsTrue),
 * so any exercise that isn't in the current program has no target to return — which is exactly what
 * happens when a coach opens a PAST program: every exercise the trainee no longer trains 404s.
 * That is unrelated to archiving; an old program that was never archived behaves identically,
 * because this endpoint never looks at the program being viewed.
 *
 * Mapping it to null instead of throwing keeps the console clean and matches what the callers
 * already do — WeekRow, ProgramExerciseCard and MobileExerciseCard all `.catch(() => <empty>)`,
 * i.e. they treat "no target" as a normal state. Real failures (401/403/5xx) still throw.
 */
export const getProgramExercise = async (traineeId, exerciseId) => {
    const {data, error, response} = await adminExerciseHistoryControllerGetCurrentProgramExercise({
        path: {traineeId, exerciseId},
    });
    if (response?.status === 404) return {data: null};
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const updateProgramExerciseRanges = async (traineeId, exerciseId, programExercise) => {
    const {data, error, response} = await adminExerciseHistoryControllerUpdateProgramExerciseRanges({
        path: {traineeId, exerciseId},
        body: programExercise,
    });
    if (error) throw asAxiosError(error, response);
    return {data};
};

// Update range by exerciseProgramId (the specific week's exercise entity)
export const updateProgramExerciseRangesById = async (exerciseProgramId, rangeData) => {
    const {data, error, response} = await adminProgramControllerUpdateExerciseRange({
        path: {exerciseProgramId},
        body: rangeData,
    });
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const getWeightSeriesInfo = async (traineeId, exerciseId) => {
    const {data, error, response} = await adminExerciseHistoryControllerGetWeightSeriesInfo({
        path: {traineeId, exerciseId},
    });
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const updateWeightSet = async (traineeId, exerciseId, weightsSet) => {
    const {data, error, response} = await adminExerciseHistoryControllerUpdateWeightSet({
        path: {traineeId, exerciseId},
        body: {weightsSet},
    });
    if (error) throw asAxiosError(error, response);
    return {data};
};
